import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ToastNotificationService } from '@core/services/toast-notification.service';
import {
  EmailVerificationService,
  VERIFY_EMAIL_REQUIRED_MESSAGE,
} from '@core/services/email-verification.service';

/** API error code for an action that needs a verified email (#943). */
export const EMAIL_NOT_VERIFIED_CODE = 'EMAIL_NOT_VERIFIED';

/**
 * Reads the `code` of an API error body. Report downloads ask for a Blob, so
 * their JSON error arrives as a Blob too and has to be read first.
 *
 * @param body The HttpErrorResponse body.
 * @returns The error code, or null.
 */
export async function apiErrorCode(body: unknown): Promise<string | null> {
  try {
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      const parsed = JSON.parse(await body.text()) as { code?: unknown };
      return typeof parsed?.code === 'string' ? parsed.code : null;
    }
    if (typeof body === 'string') {
      const parsed = JSON.parse(body) as { code?: unknown };
      return typeof parsed?.code === 'string' ? parsed.code : null;
    }
    const code = (body as { code?: unknown } | null)?.code;
    return typeof code === 'string' ? code : null;
  } catch {
    return null;
  }
}

/**
 * EmailVerificationRequiredInterceptor (#943)
 *
 * The API answers `403 EMAIL_NOT_VERIFIED` when an unverified account tries
 * to invite a teammate or export a report. This turns that into one clear
 * toast with a "Resend link" action, wherever the request came from. The
 * error still propagates so the caller can stop its own spinner.
 */
@Injectable()
export class EmailVerificationRequiredInterceptor implements HttpInterceptor {
  private toastService = inject(ToastNotificationService);
  // Resolved lazily: EmailVerificationService depends on UserService, which
  // depends on HttpClient, which depends on the interceptors.
  private injector = inject(Injector);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        if (!(err instanceof HttpErrorResponse) || err.status !== 403) {
          return throwError(() => err);
        }
        return from(apiErrorCode(err.error)).pipe(
          switchMap((code) => {
            if (code === EMAIL_NOT_VERIFIED_CODE) {
              // A warning, not an error (#956): nothing failed, a step is
              // missing, and the toast offers it.
              this.toastService.showWarning('Verify your email first', VERIFY_EMAIL_REQUIRED_MESSAGE, 12000, {
                label: 'Resend link',
                callback: () => void this.injector.get(EmailVerificationService).resend(),
              });
            }
            return throwError(() => err);
          }),
        );
      }),
    );
  }
}
